import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import { useDataTable } from 'Src/feature/data-table/useDataTable';
import { usePrinter } from 'Src/feature/printer/usePrinter';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import { useOnErrorShowToast } from 'Src/feature/toasts/useOnErrorShowToast';

import styles from './SettingsPrinters.module.scss';

const sectionId = 'SettingsPrinters';
const SettingsPrinters: FC = () => {
  const { t } = useTranslation();

  const terminalLocation = useTerminal.query.get();
  const handleMutateError = useOnErrorShowToast({ title: t('terminal.errors.updatePrinterID') });
  const printerID = useTerminal.mutate.printerID(handleMutateError);
  const printerGetAll = usePrinter.query.getAll();
  const printerSend = usePrinter.mutate.send();

  const [selectedPrinterId, selectedPrinterIdSet] = useState(terminalLocation.data?.terminal.terminalSettings.printerId);
  const selectedPrinterData = (printerGetAll.data || []).filter(({ id }) => id === selectedPrinterId);

  const printerTable = {
    selected: useDataTable({ data: selectedPrinterData }),
    all: useDataTable({ data: printerGetAll.data }),
  };

  const printTestReceipt = (printerId: number) => {
    // TODO: Handle a potential error response.
    // If the destination printer is offline, out of paper, lid open,
    // then an error will be returned
    void printerSend.mutateAsync({
      type: 'Test',
      printerId,
    });
  };

  const selectPrinter = async (printerId: number) => {
    await printerID.mutateAsync({ printerId });
    selectedPrinterIdSet(printerId);
  };

  return (
    <div data-testid={sectionId} className={styles.root}>
      <Copy color="textPrimary" variant="h6" value={t('settings.info.usingPrinter', { terminalName: terminalLocation.data?.terminal.name })} />
      <printerTable.selected.Table
        noDataMessage={t('settings.error.noPrinterSelected')}
        // there will never be more than one row in this table, so no need for pagination
        pagination={<></>}
      >
        <printerTable.selected.Column
          cellContentRenderer={({ dataItem }) => (
            <Flex horizontal alignItems="center">
              <FontIcon name="fiber_manual_record"
                className={classNames({
                  [styles.icon_online]: dataItem.status === 'Ready',
                  [styles.icon_offline]: dataItem.status !== 'Ready',
                })} />
              <Flex>
                <Copy optional value={(dataItem).name} />
                <Copy value={`${(dataItem).make} ${(dataItem).model}`} />
              </Flex>
            </Flex>
          )}
        />
        <printerTable.selected.Column field="ipAddr" />
        <printerTable.selected.Column
          align="right"
          cellContentRenderer={({ dataItem: { id, status } }) => {
            if (status === 'Ready') {
              return (
                <CTA
                  color="primary"
                  label={t('settings.labels.printTestReceipt')}
                  onClick={() => void printTestReceipt(id?.valueOf() as number)}
                />
              );
            }
          }}
        />
        <printerTable.selected.Column
          align="right"
          cellContentRenderer={() => (
            <CTA
              color="primary"
              onClick={() => void selectPrinter(0)}
              label={t('settings.labels.unassignPrinter')}
            />
          )}
        />
      </printerTable.selected.Table>

      <printerTable.all.Table
        title={t('settings.labels.localNetworkPrinters')}
        noDataMessage={t('printer.errors.getAll_noData')}
        classes={{ tableContainer: styles.table_container }}
        pagination={printerTable.all.store.data.all.length > 15 ? undefined : <></>}
      >
        <printerTable.all.Column
          cellContentRenderer={({ dataItem }) => (
            <Flex horizontal alignItems="center">
              <FontIcon name="fiber_manual_record"
                className={classNames({
                  [styles.icon_online]: (dataItem).status === 'Ready',
                  [styles.icon_offline]: (dataItem).status !== 'Ready',
                })} />
              <Copy optional value={dataItem.name} />
              <Copy value={`${dataItem.make} ${dataItem.model}`} />
            </Flex>
          )}
        />
        <printerTable.all.Column field="ipAddr" />
        <printerTable.all.Column
          align="right"
          cellContentRenderer={({ dataItem: { id, status } }) => status === 'Ready'
            ? (
              <CTA
                color="primary"
                onClick={() => void printTestReceipt(id?.valueOf() as number)}
                label={t('settings.labels.printTestReceipt')}
              />
            ) : <></>}
        />
        <printerTable.all.Column
          align="right"
          cellContentRenderer={({ dataItem: { id } }) => (
            <CTA
              color="primary"
              onClick={() => void selectPrinter(id?.valueOf() as number)}
              label={t('settings.labels.selectPrinter')}
            />
          )}
        />
      </printerTable.all.Table>
    </div>
  );
};

export default SettingsPrinters;
export { sectionId };
